import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { useSpaces, useMoodboards } from '../data/firestore';
import { CATEGORY_LABELS } from '../data/seedSpaces';
import AssetImage, { useAssetUrl } from '../components/AssetImage';
import Modal from '../components/Modal';

// Design gallery: collage samplers up top (moved here from the homepage, which
// now previews rooms), then every moodboard across every space, grouped by
// area. Read-only — editing (adding/clipping tiles, notes) happens on the
// space's own Moodboards tab, one click away.

const COLLAGES = [
  { id: 'baths', title: 'Bathrooms', spaceIds: ['bathroom-1', 'bathroom-2', 'bathroom-3', 'bathroom-4', 'powder-room'] },
  { id: 'beds', title: 'Bedrooms', spaceIds: ['bedroom-1', 'bedroom-2', 'bedroom-3', 'bedroom-4'] },
  { id: 'outdoor', title: 'Outdoor living', spaceIds: ['pool', 'sauna', 'outdoor-showers', 'back-yard', 'front-yard'] },
  { id: 'exterior', title: 'Exterior & garage', spaceIds: ['exterior', 'garage'] },
];

// Merges the moodboards of several spaces into one deduped tile sampler —
// first tile of each board first, so every style gets representation before
// any board contributes a second image.
function useCollageTiles(spaceIds, cap = 10) {
  const key = spaceIds.join(',');
  const [bySpace, setBySpace] = useState({});

  useEffect(() => {
    if (!db) return undefined;
    setBySpace({});
    const unsubs = spaceIds.map((id) =>
      db
        .collection('spaces')
        .doc(id)
        .collection('moodboards')
        .onSnapshot(
          (snap) => {
            const boards = snap.docs.map((d) => d.data());
            setBySpace((prev) => ({ ...prev, [id]: boards }));
          },
          () => {}
        )
    );
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return useMemo(() => {
    const boards = Object.entries(bySpace).flatMap(([spaceId, list]) =>
      list.map((b) => ({ spaceId, tiles: b.tiles || [] }))
    );
    const seen = new Set();
    const out = [];
    for (let round = 0; out.length < cap; round++) {
      let added = false;
      for (const b of boards) {
        if (out.length >= cap) break;
        const t = b.tiles[round];
        if (!t) continue;
        const dedupe = t.s3Key || t.url || t.id;
        if (seen.has(dedupe)) continue;
        seen.add(dedupe);
        out.push({ ...t, spaceId: b.spaceId });
        added = true;
      }
      if (!added) break;
    }
    return out;
  }, [bySpace, cap]);
}

function Collage({ title, spaceIds }) {
  const tiles = useCollageTiles(spaceIds);
  if (!tiles.length) return null;

  return (
    <section className="ah-collage">
      <div className="ah-collage-head">
        <h2 className="ah-group-title" style={{ margin: 0 }}>{title}</h2>
      </div>
      <div className="ah-collage-grid">
        {tiles.map((t) => (
          <Link
            key={`${t.spaceId}-${t.id}`}
            to={`/spaces/${t.spaceId}?tab=moodboards`}
            className="ah-collage-tile"
            title={t.name || ''}
          >
            {t.type === 'url'
              ? <img src={t.url} alt={t.name || ''} loading="lazy" />
              : <AssetImage s3Key={t.s3Key} alt={t.name || ''} />}
          </Link>
        ))}
      </div>
    </section>
  );
}

function TileLightbox({ tile, onClose }) {
  const assetUrl = useAssetUrl(tile.type === 'asset' ? tile.s3Key : null);
  const src = tile.type === 'url' ? tile.url : assetUrl;
  return (
    <Modal onClose={onClose}>
      <div className="ah-lightbox">
        {src ? <img src={src} alt={tile.name || ''} /> : <div className="ah-tile-placeholder">🖼</div>}
        <div className="ah-lightbox-side">
          {tile.name && <strong>{tile.name}</strong>}
          {tile.note && <p className="cs-help" style={{ marginTop: 6 }}>{tile.note}</p>}
          {tile.url && (
            <a href={tile.url} target="_blank" rel="noreferrer" className="cs-help" style={{ fontSize: 12 }}>
              source ↗
            </a>
          )}
        </div>
      </div>
    </Modal>
  );
}

function SpaceBoards({ space, onOpenTile }) {
  const { docs: boards } = useMoodboards(space.id);
  if (!boards || !boards.length) return null;

  return (
    <div className="cs-card" style={{ padding: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>
          {space.icon} {space.name}
        </h3>
        <span className="cs-help" style={{ fontSize: 12 }}>
          {boards.length} board{boards.length === 1 ? '' : 's'}
        </span>
        <span style={{ flex: 1 }} />
        <Link to={`/spaces/${space.id}?tab=moodboards`} className="cs-btn cs-btn-outline cs-btn-sm">
          Open space →
        </Link>
      </div>

      {boards.map((b) => (
        <div key={b.id} style={{ marginTop: 12 }}>
          <p className="cs-help" style={{ margin: '0 0 6px', fontWeight: 600 }}>
            {b.title}
            <span style={{ fontWeight: 400 }}> · {(b.tiles || []).length} tile{(b.tiles || []).length === 1 ? '' : 's'}</span>
          </p>
          <div className="ah-mood-grid">
            {(b.tiles || []).map((t) => (
              <figure key={t.id} className="ah-mood-tile" onClick={() => onOpenTile(t)}>
                {t.type === 'url'
                  ? <img src={t.url} alt={t.name || ''} loading="lazy" />
                  : <AssetImage s3Key={t.s3Key} alt={t.name || ''} />}
                {(t.note || t.name) && <figcaption>{t.name || t.note}</figcaption>}
              </figure>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Designs() {
  const { docs: spaces } = useSpaces();
  const [openTile, setOpenTile] = useState(null);

  const groups = ['interior', 'outdoor', 'exterior']
    .map((cat) => ({
      cat,
      label: CATEGORY_LABELS[cat],
      list: (spaces || []).filter((sp) => (sp.category || 'interior') === cat),
    }))
    .filter((g) => g.list.length);

  return (
    <div className="cs-page">
      <h1 style={{ marginBottom: 4 }}>Designs</h1>
      <p className="cs-help" style={{ marginTop: 0 }}>
        Every moodboard in the house, in one place. Open a space to add tiles,
        clip images from the web, or leave notes.
      </p>

      {spaces === null && <p className="cs-help">Loading…</p>}

      {COLLAGES.map((c) => <Collage key={c.id} title={c.title} spaceIds={c.spaceIds} />)}

      {groups.map((g) => (
        <section key={g.cat} style={{ marginTop: 26 }}>
          <h2 className="ah-group-title">{g.label}</h2>
          {g.list.map((sp) => (
            <SpaceBoards key={sp.id} space={sp} onOpenTile={setOpenTile} />
          ))}
        </section>
      ))}

      {openTile && <TileLightbox tile={openTile} onClose={() => setOpenTile(null)} />}
    </div>
  );
}
